import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Plus, Trash2, Save, Sparkles } from 'lucide-react';
import { useLernsets } from './LearnsetContext';

interface CardForm {
    question: string;
    answer: string;
}

const CreateFlashcards = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { getLernsetById, updateLernset } = useLernsets();
    const lernset = getLernsetById(id!);

    const [cards, setCards] = useState<CardForm[]>([]);
    const [currentQuestion, setCurrentQuestion] = useState('');
    const [currentAnswer, setCurrentAnswer] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (lernset) {
            const existingCards = lernset.cards.map(c => ({
                question: c.question,
                answer: c.answer,
            }));
            setCards(existingCards);
        }
    }, [lernset]);

    if (!lernset) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-indigo-50 p-4 pt-24 text-center">
                <h1 className="text-3xl font-bold text-gray-900 mb-4">Lernset nicht gefunden</h1>
                <Link to="/lernsets" className="text-indigo-600 hover:underline">
                    Zurück zu meinen Lernsets
                </Link>
            </div>
        );
    }

    const handleAddCard = () => {
        if (currentQuestion.trim() && currentAnswer.trim()) {
            setCards([...cards, { question: currentQuestion.trim(), answer: currentAnswer.trim() }]);
            setCurrentQuestion('');
            setCurrentAnswer('');
        }
    };

    const handleRemoveCard = (index: number) => {
        setCards(cards.filter((_, i) => i !== index));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const now = Date.now();
            const newCards = cards.map((c, idx) => ({
                id: `${now}_${idx}_${Math.random().toString(36).substr(2, 9)}`,
                question: c.question,
                answer: c.answer,
                strength: 50,
                nextDueAt: now,
                lastReviewedAt: now,
                reviewCount: 0,
                createdAt: now,
            }));
            await updateLernset(id!, { cards: newCards });
            navigate(`/lernset/${id}`);
        } catch (error) {
            console.error('Fehler beim Speichern', error);
            alert('Speichern fehlgeschlagen');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-indigo-50 p-4 pt-24">
            <div className="max-w-4xl mx-auto">
                <Link
                    to={`/lernset/${id}`}
                    className="inline-flex items-center text-gray-600 hover:text-indigo-600 mb-6 transition-colors"
                >
                    <ArrowLeft className="w-4 h-4 mr-1" />
                    Zurück zum Dashboard
                </Link>

                <div className="mb-8">
                    <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                        Karteikarten erstellen
                    </h1>
                    <p className="text-gray-600 text-lg">
                        für <span className="font-semibold text-indigo-600">{lernset.name}</span>
                    </p>
                </div>

                <div className="bg-white/70 backdrop-blur-lg rounded-2xl shadow-xl border border-white/50 p-6 mb-8">
                    <h2 className="text-xl font-semibold text-gray-800 mb-4">Neue Karte</h2>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Frage</label>
                            <textarea
                                value={currentQuestion}
                                onChange={(e) => setCurrentQuestion(e.target.value)}
                                rows={2}
                                className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white/50 focus:bg-white focus:border-indigo-300 focus:ring-4 focus:ring-indigo-100 transition-all outline-none resize-none"
                                placeholder="z.B. Was ist die Hauptstadt von Frankreich?"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Antwort</label>
                            <textarea
                                value={currentAnswer}
                                onChange={(e) => setCurrentAnswer(e.target.value)}
                                rows={2}
                                className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white/50 focus:bg-white focus:border-indigo-300 focus:ring-4 focus:ring-indigo-100 transition-all outline-none resize-none"
                                placeholder="z.B. Paris"
                            />
                        </div>
                        <div className="flex justify-end">
                            <button
                                onClick={handleAddCard}
                                disabled={!currentQuestion.trim() || !currentAnswer.trim()}
                                className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <Plus className="w-5 h-5" />
                                Karte hinzufügen
                            </button>
                        </div>
                    </div>
                </div>

                <div className="bg-white/70 backdrop-blur-lg rounded-2xl shadow-xl border border-white/50 p-6">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xl font-semibold text-gray-800">
                            Karteikarten ({cards.length})
                        </h2>
                        {cards.length > 0 && (
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="px-5 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium shadow-md transition-colors flex items-center gap-2 disabled:opacity-50"
                            >
                                <Save className="w-4 h-4" />
                                {saving ? 'Speichern...' : 'Alle speichern'}
                            </button>
                        )}
                    </div>

                    {cards.length === 0 ? (
                        <div className="text-center py-12">
                            <Sparkles className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-500">Noch keine Karteikarten hinzugefügt.</p>
                            <p className="text-gray-400 text-sm mt-1">
                                Füge oben deine erste Karte hinzu.
                            </p>
                        </div>
                    ) : (
                        <AnimatePresence>
                            <div className="space-y-3">
                                {cards.map((card, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, x: -10 }}
                                        className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm"
                                    >
                                        <div className="flex justify-between gap-4">
                                            <div className="flex-1">
                                                <p className="text-sm font-medium text-gray-500 mb-1">
                                                    Frage {index + 1}
                                                </p>
                                                <p className="text-gray-800 mb-3">{card.question}</p>
                                                <p className="text-sm font-medium text-gray-500 mb-1">
                                                    Antwort
                                                </p>
                                                <p className="text-gray-800">{card.answer}</p>
                                            </div>
                                            <button
                                                onClick={() => handleRemoveCard(index)}
                                                className="text-gray-400 hover:text-red-500 transition-colors self-start"
                                            >
                                                <Trash2 className="w-5 h-5" />
                                            </button>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </AnimatePresence>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CreateFlashcards;